
import * as Sentry from '@sentry/react';

/**
 * Error handling utilities for consistent error management
 */

export interface AppError {
  message: string;
  code?: string;
  details?: unknown;
}

export interface ErrorWithMessage {
  message: string;
}

export interface ErrorWithError {
  error: string;
}

/**
 * Type guard to check if error has message property
 */
function isErrorWithMessage(error: unknown): error is ErrorWithMessage {
  return (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as Record<string, unknown>).message === 'string'
  );
}

/**
 * Type guard to check if error has error property
 */
function isErrorWithError(error: unknown): error is ErrorWithError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'error' in error &&
    typeof (error as Record<string, unknown>).error === 'string'
  );
}

/**
 * Creates a standardized error object
 */
export const createError = (message: string, code?: string, details?: unknown): AppError => ({
  message,
  code,
  details
});

/**
 * Formats error messages for user display with proper type checking
 */
export const formatErrorMessage = (error: unknown): string => {
  if (typeof error === 'string') return error;
  
  if (isErrorWithMessage(error)) {
    return error.message;
  }
  
  if (isErrorWithError(error)) {
    return error.error;
  }
  
  return 'Ha ocurrido un error inesperado';
};

/**
 * Logs errors consistently across the application and sends to Sentry
 */
export const logError = (error: unknown, context?: string): void => {
  const formattedError = formatErrorMessage(error);
  const logMessage = context ? `${context}: ${formattedError}` : formattedError;
  
  console.error(logMessage, error);
  
  // Send to Sentry if error is an Error object
  if (error instanceof Error) {
    Sentry.captureException(error, {
      tags: {
        context: context || 'unknown',
      },
    });
  } else {
    // Send as message if not an Error object
    Sentry.captureMessage(logMessage, 'error');
  }
};

/**
 * Handles async errors with consistent logging and proper return types
 */
export const handleAsyncError = async <T>(
  asyncFn: () => Promise<T>,
  context?: string
): Promise<T | null> => {
  try {
    return await asyncFn();
  } catch (error) {
    logError(error, context);
    return null;
  }
};

/**
 * Result type for operations that can fail
 */
export type Result<T, E = AppError> = 
  | { success: true; data: T }
  | { success: false; error: E };

/**
 * Creates a success result
 */
export const createSuccessResult = <T>(data: T): Result<T> => ({
  success: true,
  data
});

/**
 * Creates an error result
 */
export const createErrorResult = <T>(error: AppError): Result<T> => ({
  success: false,
  error
});
